import type { AllRpcMethods } from "ai-vrm-chat/index.rpc";
import type { AllRpcMethods as MiscRpcMethods } from "ai-vrm-chat/index-reduced.rpc";

interface RpcRequest<P extends keyof AllRpcMethods | keyof MiscRpcMethods> {
  type: "iframe-rpc-request";
  id: string;
  path: P;
  args: (AllRpcMethods & MiscRpcMethods)[P]["args"];
}

interface RpcResponse<P extends keyof AllRpcMethods | keyof MiscRpcMethods> {
  type: "iframe-rpc-response";
  id: string;
  result: (AllRpcMethods & MiscRpcMethods)[P]["return"];
}

export class RPCManager {
  private _iframe: HTMLIFrameElement;
  private _iframeUrl: URL;
  private _listeners: Array<(event: MessageEvent) => void> = [];

  constructor(iframe: HTMLIFrameElement) {
    this._iframe = iframe;
    this._iframeUrl = new URL(iframe.src);
  }

  private static _collectTransferables(args: unknown[]): Transferable[] {
    const transferables: Transferable[] = [];

    for (const arg of args) {
      if (
        arg instanceof ArrayBuffer ||
        ArrayBuffer.isView(arg) || // TypedArray or DataView
        (typeof AudioData !== "undefined" && arg instanceof AudioData) ||
        (typeof ImageBitmap !== "undefined" && arg instanceof ImageBitmap) ||
        (typeof MediaSource !== "undefined" && arg instanceof MediaSource) ||
        (typeof MediaStreamTrack !== "undefined" &&
          arg instanceof MediaStreamTrack) ||
        (typeof MessagePort !== "undefined" && arg instanceof MessagePort) ||
        (typeof MIDIAccess !== "undefined" && arg instanceof MIDIAccess) ||
        (typeof OffscreenCanvas !== "undefined" &&
          arg instanceof OffscreenCanvas) ||
        (typeof ReadableStream !== "undefined" &&
          arg instanceof ReadableStream) ||
        (typeof RTCDataChannel !== "undefined" &&
          arg instanceof RTCDataChannel) ||
        (typeof TransformStream !== "undefined" &&
          arg instanceof TransformStream) ||
        (typeof VideoFrame !== "undefined" && arg instanceof VideoFrame) ||
        (typeof WritableStream !== "undefined" && arg instanceof WritableStream)
      ) {
        // Special case: TypedArrays need the underlying buffer
        if (ArrayBuffer.isView(arg)) {
          transferables.push(arg.buffer);
        } else {
          transferables.push(arg as Transferable);
        }
      }
    }

    return transferables;
  }

  /**
   * Calls a method inside the iframe to control or interact with the character.
   * Provides direct access to most functionality exposed by the character.
   *
   * This function requires that the character has been initialized successfully.
   * If the character failed to load and returned a `loadError`, use `postLoadErrorCall` instead.
   *
   * @template P - The key of the RPC method to call.
   * @param {P} path - Path through the object representing the method to call. TypeScript will infer valid options.
   * @param {...AllRpcMethods[P]["args"]} args - Arguments to pass to the target method.
   * @returns {Promise<AllRpcMethods[P]["return"]>} - A promise that resolves with the result of the called method.
   */
  async call<P extends keyof AllRpcMethods>(
    path: P,
    ...args: AllRpcMethods[P]["args"]
  ): Promise<AllRpcMethods[P]["return"]> {
    return this._call(path, ...args);
  }

  /**
   * Calls a method even if the character failed to load properly.
   * Use this for methods that can be invoked in error states, e.g., logging or fallback operations.
   *
   * @template P - The key of the miscellaneous RPC method to call.
   * @param {P} path - Path through the object representing the method to call. TypeScript will infer valid options.
   * @param {...MiscRpcMethods[P]["args"]} args - Arguments to pass to the target method.
   * @returns {Promise<MiscRpcMethods[P]["return"]>} - A promise that resolves with the result of the called method.
   */
  async postLoadErrorCall<P extends keyof MiscRpcMethods>(
    path: P,
    ...args: MiscRpcMethods[P]["args"]
  ): Promise<MiscRpcMethods[P]["return"]> {
    return this._call(path, ...args);
  }

  private async _call<P extends keyof AllRpcMethods | keyof MiscRpcMethods>(
    path: P,
    ...args: (MiscRpcMethods & AllRpcMethods)[P]["args"]
  ): Promise<(MiscRpcMethods & AllRpcMethods)[P]["return"]> {
    return new Promise((resolve, reject) => {
      const id = crypto.randomUUID();

      const listener = (event: MessageEvent<RpcResponse<P>>) => {
        if (event.origin !== this._iframeUrl.origin) return;
        const data = event.data;
        if (data.type === "iframe-rpc-response" && data.id === id) {
          window.removeEventListener("message", listener);
          resolve(data.result);
        }
      };

      window.addEventListener("message", listener);
      this._listeners.push(listener);

      const request: RpcRequest<P> = {
        type: "iframe-rpc-request",
        id,
        path,
        args,
      };

      const transfer = RPCManager._collectTransferables(args);

      if (this._iframe.contentWindow) {
        this._iframe.contentWindow.postMessage(request, "*", transfer);
      } else {
        reject(new Error("iframe doesn't have a 'contentWindow' member"));
      }
    });
  }

  destroy() {
    this._listeners.forEach((l) => window.removeEventListener("message", l));
    this._listeners = [];
  }
}
